import { session } from "../storage";
import { SERVICE_TIERS, tierLabel } from "../tiers";
import { useEffect, useState } from "react";
import RecordForm, { type Field } from "../components/RecordForm";
import ActivityLog from "../components/ActivityLog";
import Dialog from "../components/Dialog";
import { Globe2, Mail, Phone } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { api, formatDate, formatMoney, formatPhone } from "../api";
import { useAuth } from "../auth";
import { useClientSelection } from "../client-context";
import { DataTable, sentence } from "../components/Workflow";
import {
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/Ui";
import { useApi } from "../hooks";

type Location = {
  id: number;
  market_name: string;
  city: string | null;
  state: string | null;
  gbp_profile_url?: string | null;
  ctm_number?: string | null;
};
type Guardrail = {
  id: number;
  guardrail_type: string;
  guardrail_value: string;
  notes?: string | null;
};
type ClientDetailData = {
  id: number;
  name: string;
  industry: string | null;
  status: string;
  service_tier: string;
  monthly_ad_spend: number | null;
  primary_contact: string | null;
  primary_email: string | null;
  last_contact_at?: string | null;
  primary_phone: string | null;
  website_url?: string | null;
  booking_rate?: number | string | null;
  avg_job_value?: number | string | null;
  booking_rate_confirmed?: boolean | null;
  avg_job_value_confirmed?: boolean | null;
  locations: Location[];
  guardrails: Guardrail[];
  brand_profile: Record<string, unknown> | null;
};

/** Guardrail types in words, the same words the activity log uses. */
const GUARDRAIL_TYPE: Record<string, string> = {
  brand_rule: "Brand rule",
  forbidden_word: "Forbidden word",
  required_cta: "Required call to action",
  excluded_service: "Excluded service",
  excluded_pest: "Excluded pest",
  excluded_geography: "Excluded area",
  competitor_exclusion: "Competitor to avoid",
  visual_rule: "Visual rule",
};
const guardrailType = (t: string) => GUARDRAIL_TYPE[t] || sentence(t);
/** An archived client is stored as status "churned"; on screen it is Archived. */
const statusLabel = (s: string) => (s === "churned" ? "Archived" : sentence(s));
// Where confirmed booking numbers came from (routes/onboarding.js accepts these).
const SOURCES: Record<string, string> = {
  client_confirmed: "The client confirmed them",
  crm_data: "The client's CRM or job data",
  franchisee_report: "A franchisee report",
};
/** "Folsom, CA", or just "CA" when the city is the market's own name. */
const placeOf = (l: Location) =>
  [l.city && l.city.toLowerCase() !== l.market_name.toLowerCase() ? l.city : null, l.state].filter(Boolean).join(", ");

type Editing =
  | { kind: "contact" | "account" | "brand" | "location" | "guardrail" | "numbers" }
  | { kind: "location-edit"; location: Location };

export default function ClientDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = ["superadmin", "exec"].includes(user?.role || "");
  const admin = user?.role === "superadmin";
  const { data, loading, error, reload } = useApi<ClientDetailData>(
    `/api/clients/${id}`,
  );
  const [editing, setEditing] = useState<Editing | null>(null);
  const [activityRevision, setActivityRevision] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState("");
  const [archiving, setArchiving] = useState(false);
  const [notice, setNotice] = useState("");
  const sidebarClients = useClientSelection().clients;
  useEffect(() => {
    if (id) session.set("nl-aos-client", id);
  }, [id]);
  const saved = () => { reload(); setActivityRevision((n) => n + 1); };
  const fields: Record<string, Field[]> = {
    contact: [
      {
        key: "contact_at",
        label: "Contact date",
        type: "date",
        required: true,
      },
      {
        key: "notes",
        label: "Conversation notes",
        type: "textarea",
        required: true,
      },
    ],
    account: [
      { key: "name", label: "Client name", required: true, placeholder: "Junk King Sacramento" },
      { key: "industry", label: "Industry", half: true, placeholder: "home_services", hint: "Used to pick prompts and benchmarks." },
      {
        // Archived is set with Archive client (superadmin), not here; an archived
        // client keeps it listed so the form shows what it is.
        key: "status",
        label: "Status",
        type: "select",
        options: data?.status === "churned" ? ["active", "paused", "prospect", "churned"] : ["active", "paused", "prospect"],
        optionLabel: statusLabel,
        half: true,
      },
      { key: "primary_contact", label: "Primary contact", half: true, placeholder: "Full name" },
      { key: "primary_email", label: "Primary email", type: "email", half: true, placeholder: "owner@client.com" },
      { key: "primary_phone", label: "Primary phone", half: true, placeholder: "(916) 555-0123" },
      { key: "website_url", label: "Website", type: "url", half: true, placeholder: "https://" },
      {
        key: "monthly_ad_spend",
        label: "Monthly budget",
        type: "number",
        min: 0,
        half: true,
        placeholder: "0",
        hint: "US dollars per month, before fees.",
      },
      {
        key: "service_tier",
        label: "Service tier",
        type: "select",
        options: SERVICE_TIERS,
        optionLabel: tierLabel,
        default: "standard",
        hint: "Reporting only leaves content, creative and project pages out of this client's sidebar.",
        half: true,
      },
    ],
    numbers: [
      { key: "booking_rate", label: "Booking rate (%)", type: "number", min: 1, max: 100, required: true, half: true, placeholder: "50", hint: "Share of answered calls that book a job." },
      { key: "avg_job_value", label: "Average job value", type: "number", min: 1, required: true, half: true, placeholder: "$", hint: "US dollars per completed job." },
      { key: "source", label: "Where the numbers came from", type: "select", options: Object.keys(SOURCES), optionLabel: (o) => SOURCES[o] || sentence(o), default: "client_confirmed" },
    ],
    brand: [
      { key: "brand_voice", label: "Brand voice", type: "textarea", placeholder: "Plain, warm, no jargon. Talks like a neighbour who happens to own the business." },
      { key: "target_audience", label: "Target audience", type: "textarea", placeholder: "Homeowners 35 to 65 clearing a garage, estate or renovation." },
      { key: "usp", label: "Unique selling proposition", type: "textarea", placeholder: "Same-day pickup, upfront price, everything recycled that can be." },
      { key: "primary_color", label: "Primary color", type: "color", half: true },
      { key: "secondary_color", label: "Secondary color", type: "color", half: true },
      { key: "logo_url", label: "Logo URL", type: "url", placeholder: "https://", hint: "A PNG or SVG on a public address; it shows on reports and the portal." },
    ],
    location: [
      { key: "market_name", label: "Market name", required: true, placeholder: "Sacramento" },
      { key: "city", label: "City", half: true },
      { key: "state", label: "State", half: true, placeholder: "CA" },
      {
        key: "gbp_profile_url",
        label: "Google Business Profile URL",
        type: "url",
        placeholder: "https://",
      },
      { key: "ctm_number", label: "Tracking phone number", placeholder: "(916) 555-0123" },
    ],
    guardrail: [
      {
        key: "guardrail_type",
        label: "Type",
        type: "select",
        options: Object.keys(GUARDRAIL_TYPE),
        optionLabel: guardrailType,
      },
      {
        key: "guardrail_value",
        label: "Rule",
        type: "textarea",
        required: true,
      },
      { key: "notes", label: "Notes", type: "textarea" },
    ],
  };
  if (loading) return <LoadingState label="Loading client workspace" />;
  if (error || !data)
    return (
      <ErrorState message={error || "Client not found"} onRetry={reload} />
    );
  const brandEntries = Object.entries(data.brand_profile || {}).filter(
    ([key, value]) => ["brand_voice", "target_audience", "usp", "primary_color", "secondary_color"].includes(key) && value,
  );
  const rate = data.booking_rate == null || data.booking_rate === "" ? null : Number(data.booking_rate);
  const jobValue = data.avg_job_value == null || data.avg_job_value === "" ? null : Number(data.avg_job_value);
  const ratePct = rate == null ? null : Math.round(rate * 10000) / 100;
  const state = (confirmed: boolean | null | undefined, value: number | null) =>
    value == null ? null : <span className="detail-state">{confirmed ? "Confirmed" : "Not confirmed"}</span>;

  // Archive: out of every list, portal logins and links closed, nothing deleted.
  // Restore: active again, and the same logins and links work again.
  async function setArchived(archive: boolean) {
    setBusy(archive ? "archive" : "restore");
    setActionError("");
    setNotice("");
    try {
      await api(`/api/clients/${data!.id}`, { method: "PUT", body: JSON.stringify({ status: archive ? "churned" : "active" }) });
      setArchiving(false);
      setNotice(archive
        ? `${data!.name} is archived. Restore it from Clients, Archived.`
        : `${data!.name} is active again. Their portal logins and links work again.`);
      saved();
      sidebarClients.reload();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : archive ? "The client could not be archived." : "The client could not be restored.");
      setArchiving(false);
    } finally {
      setBusy(null);
    }
  }

  async function remove(kind: "location" | "guardrail", rowId: number, name: string) {
    const question = kind === "location"
      ? `Remove ${name} from ${data!.name}'s locations? Calls, jobs and spend already recorded for it stay.`
      : `Remove this guardrail? New drafts for ${data!.name} stop being checked against "${name}".`;
    if (!window.confirm(question)) return;
    setBusy(`${kind}-${rowId}`);
    setActionError("");
    try {
      await api(kind === "location" ? `/api/clients/${data!.id}/locations/${rowId}` : `/api/brand/${data!.id}/guardrails/${rowId}`, { method: "DELETE" });
      saved();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not remove it");
    } finally {
      setBusy(null);
    }
  }

  const form = editing && (() => {
    if (editing.kind === "location-edit") {
      const l = editing.location;
      return (
        <RecordForm
          title={`Edit ${l.market_name}`}
          description="The market, its Google Business Profile and its tracking number."
          submitLabel="Save changes"
          fields={fields.location}
          initial={l}
          endpoint={`/api/clients/${id}/locations/${l.id}`}
          method="PUT"
          onClose={() => setEditing(null)}
          onSaved={saved}
        />
      );
    }
    const kind = editing.kind;
    const copy: Record<typeof kind, { title: string; description: string; submit: string; endpoint: string; method: string; initial: Record<string, unknown> }> = {
      account: { title: "Edit account", description: `Saves to ${data.name}'s record. The status decides where the client shows up.`, submit: "Save changes", endpoint: `/api/clients/${id}`, method: "PUT", initial: data },
      brand: { title: "Edit brand", description: "What AI drafts and reports use to sound like this client.", submit: "Save changes", endpoint: `/api/brand/${id}/profile`, method: "PUT", initial: data.brand_profile || {} },
      location: { title: "Add location", description: "A territory with its own market, Google Business Profile and tracking number.", submit: "Add", endpoint: `/api/clients/${id}/locations`, method: "POST", initial: {} },
      guardrail: { title: "Add guardrail", description: "A rule every draft for this client is checked against.", submit: "Add", endpoint: `/api/brand/${id}/guardrails`, method: "POST", initial: {} },
      contact: { title: "Log contact", description: "Adds to the client's contact history.", submit: "Log contact", endpoint: `/api/clients/${id}/contact`, method: "POST", initial: {} },
      numbers: {
        title: "Confirm numbers",
        description: "The booking rate and average job value the client confirmed. ROAS and the AI analyst use only confirmed numbers.",
        submit: "Confirm",
        endpoint: `/api/onboarding/${id}/confirm-data`,
        method: "PUT",
        initial: { booking_rate: ratePct ?? "", avg_job_value: jobValue ?? "" },
      },
    };
    const c = copy[kind];
    return (
      <RecordForm
        title={c.title}
        description={c.description}
        submitLabel={c.submit}
        fields={fields[kind]}
        initial={c.initial}
        endpoint={c.endpoint}
        method={c.method}
        onClose={() => setEditing(null)}
        onSaved={saved}
      />
    );
  })();

  return (
    <>
      {/* The breadcrumb and the sidebar's All clients lead back; no third way back here. */}
      <PageHeader
        title={data.name}
        description={data.industry ? sentence(data.industry) : undefined}
        actions={
          // Whole-client actions live up here; each panel below carries its own.
          // The work queue link stays because the queue has no sidebar row inside a client.
          <div className="detail-header-actions client-header-actions">
            <StatusBadge tone={data.status === "active" ? "success" : "neutral"}>
              {statusLabel(data.status)}
            </StatusBadge>
            <Link className="button secondary" to={`/tasks?client_id=${data.id}`}>Work queue</Link>
            {admin && (data.status === "churned"
              ? <button className="button secondary" disabled={busy === "restore"} onClick={() => void setArchived(false)}>{busy === "restore" ? "Restoring…" : "Restore"}</button>
              : <button className="button secondary" onClick={() => { setActionError(""); setArchiving(true); }}>Archive client</button>)}
            {canEdit && data.status !== "churned" && <button className="button" onClick={() => setEditing({ kind: "contact" })}>Log contact</button>}
          </div>
        }
      />
      {form}
      {archiving && (
        <Dialog
          title={`Archive ${data.name}?`}
          description="They leave every list, and their portal logins and links stop working. Nothing is deleted. You can restore them from Clients, Archived."
          onClose={() => setArchiving(false)}
        >
          <div className="operation-form">
            <div className="operation-actions">
              <button type="button" className="button secondary" onClick={() => setArchiving(false)}>Cancel</button>
              <button type="button" className="button danger" disabled={busy === "archive"} onClick={() => void setArchived(true)}>{busy === "archive" ? "Archiving…" : "Archive client"}</button>
            </div>
          </div>
        </Dialog>
      )}
      {notice && <p className="inline-notice success" role="status">{notice}</p>}
      {actionError && <p className="inline-notice danger" role="alert">{actionError}</p>}
      <div className="detail-grid client-profile">
        <section className="panel detail-summary">
          <div className="panel-heading">
            <div>
              <h2>Account overview</h2>
            </div>
            <div className="detail-heading-actions">
              {canEdit && <button className="button secondary" onClick={() => setEditing({ kind: "numbers" })}>Confirm numbers</button>}
              {canEdit && <button className="button secondary" onClick={() => setEditing({ kind: "account" })}>Edit</button>}
            </div>
          </div>
          <dl className="detail-list">
            <div>
              <dt>Service tier</dt>
              <dd>{tierLabel(data.service_tier)}</dd>
            </div>
            <div>
              <dt>Monthly budget</dt>
              <dd>{formatMoney(data.monthly_ad_spend)}</dd>
            </div>
            <div>
              <dt>Last recorded contact</dt>
              <dd>
                {data.last_contact_at ? formatDate(data.last_contact_at) : "Not recorded"}
              </dd>
            </div>
            <div>
              <dt>Primary contact</dt>
              <dd>{data.primary_contact || "Not assigned"}</dd>
            </div>
            <div>
              <dt>Booking rate</dt>
              <dd>{ratePct == null ? "Not recorded" : `${ratePct}%`}{state(data.booking_rate_confirmed, ratePct)}</dd>
            </div>
            <div>
              <dt>Average job value</dt>
              <dd>{jobValue == null ? "Not recorded" : formatMoney(jobValue)}{state(data.avg_job_value_confirmed, jobValue)}</dd>
            </div>
          </dl>
          <div className="contact-stack">
            {data.primary_email && (
              <a href={`mailto:${data.primary_email}`}>
                <Mail aria-hidden="true" />
                {data.primary_email}
              </a>
            )}
            {data.primary_phone && (
              <a href={`tel:${data.primary_phone}`}>
                <Phone aria-hidden="true" />
                {formatPhone(data.primary_phone)}
              </a>
            )}
            {data.website_url && (
              <a href={data.website_url} target="_blank" rel="noreferrer">
                <Globe2 aria-hidden="true" />
                Visit website
              </a>
            )}
          </div>
        </section>
        <section className="panel client-locations">
          <div className="panel-heading">
            <div>
              <h2>Locations</h2>
            </div>
            {canEdit && <button className="button secondary" onClick={() => setEditing({ kind: "location" })}>Add location</button>}
          </div>
          <DataTable
            rows={data.locations.map((l) => ({
              id: l.id,
              market: <span className="location-name"><strong>{l.market_name}</strong>{placeOf(l) && <span>{placeOf(l)}</span>}</span>,
              tracking_phone: l.ctm_number ? formatPhone(l.ctm_number) : null,
              google_profile: l.gbp_profile_url ? <a href={l.gbp_profile_url} target="_blank" rel="noreferrer">Open profile</a> : null,
            }))}
            columns={["market", "tracking_phone", "google_profile"]}
            empty="No locations yet. Add each market with its own Google Business Profile and tracking number."
            action={canEdit ? (row) => {
              const l = data.locations.find((x) => x.id === row.id)!;
              return (
                <span className="row-actions">
                  <button type="button" className="text-button" onClick={() => setEditing({ kind: "location-edit", location: l })}>Edit</button>
                  <button type="button" className="text-button danger" disabled={busy === `location-${l.id}`} onClick={() => void remove("location", l.id, l.market_name)}>
                    {busy === `location-${l.id}` ? "Removing…" : "Remove"}
                  </button>
                </span>
              );
            } : undefined}
          />
        </section>
        <section className="panel detail-wide">
          <div className="panel-heading">
            <div>
              <h2>Brand profile</h2>
            </div>
            {canEdit && <button className="button secondary" onClick={() => setEditing({ kind: "brand" })}>Edit brand</button>}
          </div>
          {brandEntries.length ? (
            <dl className="record-fields">
              {brandEntries.map(([key, value]) => (
                <div key={key}>
                  <dt>{key === "usp" ? "Selling points" : sentence(key)}</dt>
                  <dd>{String(value)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="workflow-note">
              No brand guidance yet. Add the brand voice, audience and selling points so AI drafts sound like this client.
            </p>
          )}
        </section>
        <section className="panel detail-wide client-guardrails">
          <div className="panel-heading">
            <div>
              <h2>Client guardrails</h2>
            </div>
            <div className="detail-heading-actions">
              <span className="panel-meta">{data.guardrails.length} active</span>
              {canEdit && <button className="button secondary" onClick={() => setEditing({ kind: "guardrail" })}>Add guardrail</button>}
            </div>
          </div>
          <DataTable
            rows={data.guardrails.map((g) => ({ id: g.id, type: guardrailType(g.guardrail_type), rule: g.guardrail_value, notes: g.notes || null }))}
            columns={data.guardrails.some((g) => g.notes) ? ["type", "rule", "notes"] : ["type", "rule"]}
            empty="No client-specific guardrails yet. Add the words, services and areas every draft must avoid or include."
            action={canEdit ? (row) => (
              <button type="button" className="text-button danger" disabled={busy === `guardrail-${row.id}`} onClick={() => void remove("guardrail", Number(row.id), String(row.rule))}>
                {busy === `guardrail-${row.id}` ? "Removing…" : "Remove"}
              </button>
            ) : undefined}
          />
        </section>
        {/* The record first (brand, guardrails), then what happened to it: a long feed never pushes them down. */}
        <ActivityLog clientId={data.id} refresh={activityRevision} />
      </div>
    </>
  );
}
